import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield, Users, Clock, Bath, Move, DoorOpen, Home as HomeIcon, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { initUTMTracking, getUTMParamsForSubmission } from '../utils/utmTracking';
import { trackCallClick, getContactFormId, trackContactFormStart, trackContactFormSubmit } from '../utils/tracking';
import SEOHead from '../components/SEOHead';
import { LocalBusinessSchema } from '../components/SchemaOrg';

const VideoCard = ({ video }) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const thumbUrl = video.platform === 'youtube'
    ? `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`
    : `https://www.dailymotion.com/thumbnail/video/${video.id}`;
  const embedUrl = video.platform === 'dailymotion'
    ? `https://geo.dailymotion.com/player.html?video=${video.id}&autoplay=1&mute=0`
    : `https://www.youtube.com/embed/${video.id}?autoplay=1`;

  return (
    <div className="flex-shrink-0 w-80 snap-start" data-testid={video.testid}>
      <div
        className="relative rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-black cursor-pointer"
        style={{ paddingTop: '56.25%' }}
        onClick={() => !isPlaying && setIsPlaying(true)}
      >
        {isPlaying ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={embedUrl}
            title={video.title}
            allow="autoplay; fullscreen; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        ) : (
          <>
            <img src={thumbUrl} alt={video.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center hover:bg-black/20 transition-colors">
              <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                <div className="w-0 h-0 border-t-[10px] border-t-transparent border-l-[18px] border-l-[#033B5D] border-b-[10px] border-b-transparent ml-1"></div>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="mt-3 px-1">
        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">{video.name}</span>
        <p className="text-sm font-medium text-[#033B5D] mt-1 leading-snug">{video.title}</p>
      </div>
    </div>
  );
};

const NewHome = () => {
  const navigate = useNavigate();
  const scrollRef = React.useRef(null);
  const videoScrollRef = React.useRef(null);
  const homeFormIdRef = React.useRef(getContactFormId());
  const homeFormStartedRef = React.useRef(false);

  const handleHomeFormFocus = () => {
    if (!homeFormStartedRef.current) {
      homeFormStartedRef.current = true;
      trackContactFormStart(homeFormIdRef.current, '/');
    }
  };

  useEffect(() => {
    initUTMTracking();
  }, []);

  const [contactForm, setContactForm] = React.useState({
    nom: '',
    telephone: '',
    email: '',
    age: '',
    nbPersonnes: '',
    typeTravaux: '',
    codePostal: '',
    ville: '',
    revenuFiscal: ''
  });
  const [homeCommunes, setHomeCommunes] = React.useState([]);
  const [homeLoadingCommunes, setHomeLoadingCommunes] = React.useState(false);

  const handleContactChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
    if (field === 'codePostal' && value.length === 5 && /^\d{5}$/.test(value)) {
      setHomeLoadingCommunes(true);
      setHomeCommunes([]);
      fetch(`https://geo.api.gouv.fr/communes?codePostal=${value}&fields=nom,codeDepartement`)
        .then(res => res.json())
        .then(data => {
          setHomeCommunes(data);
          if (data.length === 1) {
            setContactForm(prev => ({ ...prev, ville: data[0].nom }));
          }
        })
        .catch(() => setHomeCommunes([]))
        .finally(() => setHomeLoadingCommunes(false));
    } else if (field === 'codePostal') {
      setHomeCommunes([]);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    trackContactFormSubmit(homeFormIdRef.current, '/');
    const utmParams = getUTMParamsForSubmission();
    
    const leadData = {
      nom: contactForm.nom,
      telephone: contactForm.telephone,
      email: contactForm.email,
      age: contactForm.age,
      nb_personnes: contactForm.nbPersonnes,
      type_travaux: contactForm.typeTravaux,
      code_postal: contactForm.codePostal,
      ville: contactForm.ville,
      revenu_fiscal: contactForm.revenuFiscal,
      ...utmParams,
      page_url: window.location.href,
      submitted_at: new Date().toISOString(),
      source: 'homepage-eligibilite-form'
    };

    try {
      const API_URL = window.location.origin;
      const res = await fetch(`${API_URL}/api/webhooks/landing-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadData)
      });
      if (res.status === 409) {
        toast.info('Votre demande est déjà en cours de traitement. Pour suivre son avancement, appelez le 09.70.70.70.69 (touche 1).', { duration: 8000 });
      } else {
        if (typeof window.gtag_report_conversion === 'function') window.gtag_report_conversion();
        toast.success('Merci ! Un agent va vérifier votre éligibilité et vous recontacter rapidement.');
      }
      setContactForm({ nom: '', telephone: '', email: '', age: '', nbPersonnes: '', typeTravaux: '', codePostal: '', ville: '', revenuFiscal: '' });
      setHomeCommunes([]);
    } catch (err) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    }
  };

  const travauxFinances = [
    {
      icon: Bath,
      title: 'Douche sécurisée',
      description: 'Installation d\'une douche à l\'italienne adaptée aux seniors',
      link: '/douche-senior'
    },
    {
      icon: Shield,
      title: 'Volets roulants électriques',
      description: 'Motorisation des volets pour plus de confort et d\'autonomie au quotidien',
      link: '/ma-prime-adapt-volets-roulants-electriques-call'
    },
    {
      icon: Move,
      title: 'Monte-escalier',
      description: 'Pour accéder facilement aux étages de votre maison',
      link: '/monte-escalier'
    },
    {
      icon: DoorOpen,
      title: 'Adaptation salle de bain',
      description: 'Rénovation complète pour plus de confort et sécurité',
      link: '/adaptation-salle-de-bain'
    },
    {
      icon: HomeIcon,
      title: 'Aménagement complet',
      description: 'Adaptation globale de votre logement',
      link: '/aide-adaptation-logement'
    }
  ];

  const commentCaMarche = [
    {
      number: '1',
      title: 'Vérification de votre éligibilité',
      description: 'Un agent dédié analyse votre situation personnelle en fonction des barèmes en vigueur (revenus, âge, situation de handicap). Chaque cas est unique : nous vérifions vos droits pour vous garantir un accompagnement adapté.',
      detail: 'Simulateur en ligne + rappel gratuit'
    },
    {
      number: '2',
      title: 'Audit gratuit à domicile',
      description: 'Nous envoyons un auditeur gratuitement chez vous pour vérifier l\'éligibilité administrative et technique de votre projet. Il s\'assure que les travaux sont réalisables et conformes aux exigences. Nous vous accompagnons dans l\'ouverture de votre compte sur monprojet.anah.gouv.fr et le mandatement d\'un AMO (Assistant à Maîtrise d\'Ouvrage).',
      detail: 'Visite gratuite et sans engagement'
    },
    {
      number: '3',
      title: 'Visite technique de l\'AMO',
      description: 'L\'AMO habilité par l\'Anah effectue une visite technique approfondie : état des lieux du logement, vérification de la conformité du projet et contrôle anti-fraude. Il constitue et dépose ensuite votre dossier complet auprès de l\'Anah.',
      detail: 'Dossier déposé à l\'Anah'
    },
    {
      number: '4',
      title: 'Validation des matériaux par un architecte',
      description: 'Un(e) architecte se déplace à votre domicile pour valider le choix des matériaux et s\'assurer que les solutions retenues correspondent parfaitement à vos besoins et aux normes en vigueur.',
      detail: 'Visite personnalisée à domicile'
    },
    {
      number: '5',
      title: 'Accord de l\'Anah',
      description: 'L\'Anah (Agence Nationale de l\'Habitat) examine votre dossier et octroie officiellement l\'aide financière. Vous recevez la notification d\'accord avec le montant exact de votre subvention Ma Prime Adapt\'.',
      detail: 'Jusqu\'à 15 400 € accordés'
    },
    {
      number: '6',
      title: 'Réalisation des travaux',
      description: 'Nos professionnels certifiés réalisent les travaux d\'adaptation convenus. Installation soignée, respect des délais et du cahier des charges validé. Vous profitez d\'un logement adapté, sécurisé et confortable.',
      detail: 'Artisans certifiés et garantis'
    }
  ];

  const reassurancePoints = [
    {
      icon: Shield,
      title: 'Accompagnement complet',
      description: 'De la demande d\'aide à l\'installation des travaux'
    },
    {
      icon: CheckCircle,
      title: 'Service 100% gratuit',
      description: 'Aucun frais pour l\'étude de votre dossier'
    },
    {
      icon: Users,
      title: 'Experts certifiés',
      description: 'Plus de 15 ans d\'expérience dans l\'adaptation de logements'
    }
  ];

  const faqItems = [
    {
      question: 'Quelles sont les aides disponibles pour adapter mon logement ?',
      answer: 'Plusieurs aides existent : Ma Prime Adapt\' (jusqu\'à 15 400€), les aides de l\'Anah, les subventions locales, et les prêts à taux zéro. Le montant dépend de vos revenus, de votre âge et du type de travaux.'
    },
    {
      question: 'Suis-je éligible aux aides si je suis locataire ?',
      answer: 'Oui, certaines aides sont accessibles aux locataires, notamment pour des travaux d\'adaptation. Votre propriétaire devra donner son accord. Utilisez notre simulateur pour connaître vos droits.'
    },
    {
      question: 'Combien de temps prennent les démarches ?',
      answer: 'Une fois votre dossier complet, le traitement prend généralement 2 à 4 semaines. Nous vous accompagnons à chaque étape pour accélérer le processus. Les travaux peuvent débuter sous 48h après validation.'
    },
    {
      question: 'Quels types de travaux sont financés ?',
      answer: 'Douche sécurisée, monte-escalier, adaptation salle de bain, rampes d\'accès, élargissement de portes, barres d\'appui, volets électriques, et bien d\'autres adaptations pour le maintien à domicile.'
    },
    {
      question: 'Y a-t-il des frais pour votre service ?',
      answer: 'Non, notre service d\'accompagnement est entièrement gratuit. Nous sommes rémunérés par nos partenaires installateurs. Vous ne payez que la part des travaux non couverte par les aides.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead 
        title="Prime Adapt Seniors | Adaptation logement senior - Ma Prime Adapt' jusqu'a 15 400 euros" 
        description="Accompagnement gratuit pour adapter votre logement. Douche senior, monte-escalier, rampe d'acces. Jusqu'a 15 400 euros d'aides avec Ma Prime Adapt'. Devis gratuit." 
      />
      <LocalBusinessSchema />
      <section className="relative bg-gradient-to-br from-[#033B5D] via-[#044d75] to-[#055a8e] text-white py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
              Quel est le montant de Ma Prime Adapt' pour améliorer mon logement existant ?
            </h1>
            <p className="text-lg md:text-xl mb-4 text-gray-200">
              Accompagnement gratuit dans vos démarches administratives
            </p>
            <p className="text-base md:text-lg text-gray-300 mb-6">
              Jusqu'à <span className="text-amber-400 font-bold text-2xl">15 400€ d'aides</span> pour vos travaux d'adaptation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/verifier-eligibilite')}
                size="lg"
                data-testid="hero-cta-eligibility"
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-105"
              >
                Vérifier mon éligibilité
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                data-testid="hero-cta-callback"
                className="bg-white/10 backdrop-blur-sm border-2 border-white hover:bg-white/20 text-white px-8 py-6 text-lg font-semibold rounded-xl"
              >
                <Phone className="mr-2 h-5 w-5" />
                Être rappelé gratuitement
              </Button>
            </div>

            {/* Bandeau CTA Allô */}
            <div className="mt-10 flex flex-col items-center gap-3">
              <a
                href="tel:0970707069"
                data-testid="hero-cta-phone-banner"
                className="block rounded-2xl overflow-hidden shadow-2xl hover:shadow-amber-400/30 transition-all duration-300 hover:scale-[1.02] bg-white max-w-xl w-full"
              >
                <img
                  src="/images/allobandeau.webp"
                  alt="Allô je veux connaître mes aides - Appelez le 09.70.70.70.69 prix d'un appel local"
                  className="w-full h-auto"
                  loading="eager"
                />
              </a>
              <p className="text-white/80 text-sm text-center max-w-xl leading-snug">
                Service privé d'information et de mise en relation concernant les aides à l'adaptation du logement (MaPrimeAdapt').
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Travaux financés */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#033B5D] mb-4">
              Travaux financés par les aides
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Découvrez les principales adaptations éligibles aux aides financières
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {travauxFinances.map((travail, index) => {
              const Icon = travail.icon;
              return (
                <Link
                  key={index}
                  to={travail.link}
                  className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                >
                  <div className="w-16 h-16 bg-[#033B5D] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="text-lg font-bold text-[#033B5D] mb-2">{travail.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{travail.description}</p>
                  <span className="text-green-600 font-semibold text-sm flex items-center group-hover:gap-2 transition-all">
                    En savoir plus
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:ml-2 transition-all" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Éligibilité */}
      <section className="py-10 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 flex justify-center">
          <Link
            to="/verifier-eligibilite"
            data-testid="cta-eligibilite-banner"
            className="group relative bg-gradient-to-r from-green-600 to-emerald-500 rounded-full px-10 py-5 shadow-2xl hover:shadow-green-500/40 hover:scale-105 transition-all duration-300 flex items-center gap-4"
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
              <CheckCircle className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="text-white text-xl font-bold leading-tight">Suis-je éligible ? Je fais le test gratuit</p>
              <p className="text-white/75 text-sm">Résultat immédiat — 2 minutes</p>
            </div>
            <ArrowRight className="w-6 h-6 text-white ml-2 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Bloc Rassurance */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {reassurancePoints.map((point, index) => {
              const Icon = point.icon;
              return (
                <div key={index} className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Icon className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-[#033B5D] mb-2">{point.title}</h3>
                  <p className="text-gray-600">{point.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#033B5D] mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Un accompagnement complet en 6 étapes, de l'éligibilité à la réalisation des travaux
            </p>
            <p className="mt-4 text-sm font-semibold text-green-700 bg-green-50 inline-block px-4 py-2 rounded-full">
              99 % des projets éligibles aboutissent — sauf en cas de fraude documentaire du bénéficiaire
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {commentCaMarche.map((etape, index) => (
              <div key={index} className="relative flex gap-6 mb-8 last:mb-0">
                {/* Timeline line */}
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-[#033B5D] to-[#044d75] rounded-full flex items-center justify-center text-white text-lg md:text-xl font-bold shrink-0 shadow-lg z-10">
                    {etape.number}
                  </div>
                  {index < commentCaMarche.length - 1 && (
                    <div className="w-0.5 flex-1 bg-gradient-to-b from-[#033B5D] to-green-400 mt-2"></div>
                  )}
                </div>

                {/* Content card */}
                <div className="bg-white rounded-2xl shadow-md p-6 flex-1 border-l-4 border-green-500 hover:shadow-lg transition-shadow duration-300 mb-2">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <h3 className="text-lg font-bold text-[#033B5D]">{etape.title}</h3>
                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full whitespace-nowrap shrink-0">{etape.detail}</span>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base">{etape.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/verifier-eligibilite')}
              size="lg"
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-6 text-lg font-semibold rounded-xl"
            >
              Je commence maintenant
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Avantages Prime Adapt Seniors */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#033B5D] mb-4">
              Les avantages de choisir Prime Adapt Seniors
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Un réseau d'artisans qualifiés, sélectionnés et certifiés pour répondre aux normes imposées par l'Anah
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-[#033B5D] to-[#044d75] rounded-2xl p-6 text-white shadow-xl">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Artisans qualifiés et conformes</h3>
              <p className="text-white/80 text-sm leading-relaxed">Nos artisans maîtrisent les exigences de l'Anah : réalisation de devis conformes, choix de matériaux certifiés et respect des normes d'accessibilité en vigueur.</p>
            </div>

            <div className="bg-gradient-to-br from-[#033B5D] to-[#044d75] rounded-2xl p-6 text-white shadow-xl">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Matériaux certifiés par nos soins</h3>
              <p className="text-white/80 text-sm leading-relaxed">Chaque matériau est vérifié et validé par notre équipe pour garantir sa conformité aux cahiers des charges de l'Anah et sa durabilité dans le temps.</p>
            </div>

            <div className="bg-gradient-to-br from-[#033B5D] to-[#044d75] rounded-2xl p-6 text-white shadow-xl">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Accompagnement de A à Z</h3>
              <p className="text-white/80 text-sm leading-relaxed">Un interlocuteur unique vous guide à chaque étape : de la vérification d'éligibilité jusqu'à la réception des travaux, sans que vous n'ayez à gérer les démarches.</p>
            </div>

            <div className="bg-gradient-to-br from-[#033B5D] to-[#044d75] rounded-2xl p-6 text-white shadow-xl">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Devis conformes aux exigences</h3>
              <p className="text-white/80 text-sm leading-relaxed">Les devis sont rédigés selon les normes imposées par l'Anah, évitant tout rejet de dossier lié à des erreurs administratives ou des non-conformités.</p>
            </div>

            <div className="bg-gradient-to-br from-[#033B5D] to-[#044d75] rounded-2xl p-6 text-white shadow-xl">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Aucun frais avancé</h3>
              <p className="text-white/80 text-sm leading-relaxed">Notre service d'accompagnement est entièrement gratuit. Vous ne payez que la part des travaux non couverte par les aides, après validation de votre dossier.</p>
            </div>

            <div className="bg-gradient-to-br from-[#033B5D] to-[#044d75] rounded-2xl p-6 text-white shadow-xl">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Garantie et suivi post-travaux</h3>
              <p className="text-white/80 text-sm leading-relaxed">Tous les travaux sont couverts par une garantie. Notre équipe assure un suivi après l'installation pour s'assurer de votre entière satisfaction.</p>
            </div>
          </div>

          {/* Bloc aides complémentaires mis en avant */}
          <div className="max-w-6xl mx-auto mt-8">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center shrink-0">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2">Recherche d'aides complémentaires pour réduire votre reste à charge</h3>
                  <p className="text-white/90 leading-relaxed">Notre département spécialisé recherche activement toutes les aides complémentaires auxquelles vous avez droit : <strong>caisse de retraite complémentaire, CNAV, CARSAT, aides départementales, PCH (Prestation de Compensation du Handicap)</strong> et autres dispositifs locaux. Même si vous êtes éligible à 70 % par Ma Prime Adapt', ces aides cumulées permettent de réduire drastiquement votre reste à charge — parfois jusqu'à 0 €.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nos réalisations - Carrousel */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="inline-block bg-green-500 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider mb-4">
              Nos chantiers
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#033B5D] mb-3">
              Ils nous ont fait confiance
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Découvrez quelques-unes de nos réalisations en adaptation de logement
            </p>
          </div>

          <div className="relative max-w-6xl mx-auto">
            <button
              onClick={() => {
                const el = document.getElementById('realisations-carousel');
                if (el) el.scrollBy({ left: -300, behavior: 'smooth' });
              }}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-all hidden md:flex items-center justify-center"
              aria-label="Précédent"
            >
              <ChevronLeft className="h-6 w-6 text-[#033B5D]" />
            </button>
            <button
              onClick={() => {
                const el = document.getElementById('realisations-carousel');
                if (el) el.scrollBy({ left: 300, behavior: 'smooth' });
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-all hidden md:flex items-center justify-center"
              aria-label="Suivant"
            >
              <ChevronRight className="h-6 w-6 text-[#033B5D]" />
            </button>
            <div
              id="realisations-carousel"
              className="flex gap-5 overflow-x-auto scroll-smooth pb-4 scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {[
                { img: '/images/realisations/sdb-confort-1.jpeg', title: 'Salle de bain Confort', desc: 'Douche plain-pied sécurisée' },
                { img: '/images/realisations/sdb-premium-1.jpeg', title: 'Salle de bain Premium', desc: 'Revêtement GERFLOR & GROHE' },
                { img: '/images/realisations/sdb-premium-3.jpeg', title: 'Rénovation complète', desc: 'Reprise plomberie & finitions' },
                { img: '/images/realisations/wc-geberit.jpeg', title: 'WC suspendu Geberit', desc: 'Rehaussement & accessibilité' },
                { img: '/images/realisations/monte-escalier-1.jpeg', title: 'Monte-escalier', desc: 'Rail sur mesure & sécurité' },
                { img: '/images/realisations/volets-solaires-1.jpeg', title: 'Volets roulants solaires', desc: 'Motorisation sans effort' },
                { img: '/images/realisations/finitions-1.jpeg', title: 'Finitions soignées', desc: 'Contrôle qualité systématique' },
                { img: '/images/realisations/sdb-confort-2.jpeg', title: 'Douche à l\'italienne', desc: 'Sol antidérapant & paroi vitrée' },
              ].map((item, i) => (
                <Link
                  to="/nos-realisations"
                  key={i}
                  className="flex-shrink-0 w-72 group"
                >
                  <div className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={item.img}
                        alt={`${item.title} - Prime Adapt Seniors`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-[#033B5D] text-sm">{item.title}</h3>
                      <p className="text-gray-500 text-xs mt-1">{item.desc}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/nos-realisations"
              className="inline-flex items-center gap-2 bg-[#033B5D] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#044d75] transition-all duration-300 hover:shadow-lg"
            >
              Voir toutes nos réalisations
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Attention aux arnaques */}
      <section className="py-16 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <Shield className="w-4 h-4" />
                Information importante
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#033B5D] mb-4">
                Attention aux arnaques liées à Ma Prime Adapt'
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Le succès du dispositif attire malheureusement des escrocs. Protégez-vous en choisissant un accompagnement fiable et traçable.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              {/* Signaux d'alerte */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-red-500">
                <h3 className="text-lg font-bold text-red-600 mb-4">Les signaux d'alerte</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="text-red-500 font-bold mt-0.5">x</span>
                    <span className="text-gray-700 text-sm">Démarchage agressif par téléphone ou à domicile sans sollicitation de votre part</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-500 font-bold mt-0.5">x</span>
                    <span className="text-gray-700 text-sm">Demande de paiement immédiat ou d'avance de fonds avant toute visite technique</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-500 font-bold mt-0.5">x</span>
                    <span className="text-gray-700 text-sm">Promesse d'une prise en charge à 100 % sans vérification de votre situation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-500 font-bold mt-0.5">x</span>
                    <span className="text-gray-700 text-sm">Absence de numéro de dossier ou de suivi traçable de votre projet</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-500 font-bold mt-0.5">x</span>
                    <span className="text-gray-700 text-sm"><strong>Ne signez aucun devis, bon de commande et encore moins de chèque</strong> avant que l'Assistant à Maîtrise d'Ouvrage (AMO), habilité par l'État, ne soit passé chez vous</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-500 font-bold mt-0.5">x</span>
                    <span className="text-gray-700 text-sm"><strong>N'envoyez jamais vos documents personnels</strong> (pièce d'identité, avis d'imposition, taxe foncière) lors d'un premier contact avec une entreprise non vérifiée</span>
                  </li>
                </ul>
              </div>

              {/* Avec nous */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500">
                <h3 className="text-lg font-bold text-green-600 mb-4">Avec Prime Adapt Seniors, vous êtes protégé</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm"><strong>Numéro de dossier unique</strong> attribué dès la prise en charge — votre garantie de traçabilité et de protection contre les escroqueries</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm"><strong>AMO habilité par l'Anah</strong> qui contrôle chaque étape et vérifie la conformité du projet</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm"><strong>Artisans vérifiés et certifiés</strong> avec devis conformes aux exigences de l'Anah</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm"><strong>Suivi complet</strong> de votre dossier via la plateforme officielle monprojet.anah.gouv.fr</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm"><strong>Validation du devis uniquement lors du passage de l'architecte</strong>, après validation de votre dossier par l'AMO — aucune signature précipitée</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm"><strong>Suivi approfondi de toutes les étapes</strong> via les sites internet de nos artisans partenaires</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm"><strong>Tous les appels et échanges sont consignés</strong> dans le respect des normes RGPD, pour votre protection et votre tranquillité</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-[#033B5D] rounded-2xl p-6 text-center text-white shadow-xl">
              <p className="text-lg font-semibold mb-1">Vous avez un doute sur une démarche ou une entreprise ?</p>
              <p className="text-white/80 text-sm">Appelez-nous au <a href="tel:0970707069" className="font-bold text-amber-400 hover:underline">09.70.70.70.69</a> — nous vous confirmerons si votre interlocuteur est légitime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-2 border-[#033B5D]">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-[#033B5D] mb-4">
                Je veux savoir si je suis éligible
              </h2>
              <p className="text-lg text-gray-600">
                Remplissez ce formulaire, un agent vérifie votre éligibilité et vous recontacte
              </p>
            </div>

            <form onSubmit={handleContactSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-age" className="text-base font-semibold">Âge *</Label>
                  <Input id="contact-age" type="number" required min="18" max="120"
                    value={contactForm.age} onChange={(e) => handleContactChange('age', e.target.value)}
                    className="mt-2 text-lg py-5" placeholder="72" />
                </div>
                <div>
                  <Label htmlFor="contact-nbpersonnes" className="text-base font-semibold">Personnes dans le foyer *</Label>
                  <select id="contact-nbpersonnes" required value={contactForm.nbPersonnes}
                    onChange={(e) => handleContactChange('nbPersonnes', e.target.value)}
                    className="mt-2 w-full text-lg py-3 px-3 border border-gray-200 rounded-md">
                    <option value="">Sélectionner</option>
                    <option value="1">1 personne</option>
                    <option value="2">2 personnes</option>
                    <option value="3">3 personnes</option>
                    <option value="4">4 personnes</option>
                    <option value="5">5 personnes ou +</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="contact-travaux" className="text-base font-semibold">Type de travaux souhaités *</Label>
                <select id="contact-travaux" required value={contactForm.typeTravaux}
                  onChange={(e) => handleContactChange('typeTravaux', e.target.value)}
                  className="mt-2 w-full text-lg py-3 px-3 border border-gray-200 rounded-md">
                  <option value="">Sélectionner</option>
                  <option value="Douche sécurisée">Douche sécurisée pour senior</option>
                  <option value="Volets roulants électriques">Volets roulants électriques</option>
                  <option value="Monte-escalier">Monte-escalier</option>
                  <option value="Adaptation salle de bain">Adaptation salle de bain complète</option>
                  <option value="Aménagement complet">Aménagement complet du logement</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-cp" className="text-base font-semibold">Code postal *</Label>
                  <Input id="contact-cp" type="text" required maxLength="5"
                    value={contactForm.codePostal} onChange={(e) => handleContactChange('codePostal', e.target.value.replace(/\D/g, ''))}
                    className="mt-2 text-lg py-5" placeholder="75001" />
                  {homeLoadingCommunes && <p className="text-sm text-gray-400 mt-1">Recherche...</p>}
                </div>
                <div>
                  <Label htmlFor="contact-ville" className="text-base font-semibold">Ville *</Label>
                  {homeCommunes.length > 1 ? (
                    <select id="contact-ville" required value={contactForm.ville}
                      onChange={(e) => setContactForm(prev => ({ ...prev, ville: e.target.value }))}
                      className="mt-2 w-full text-lg py-3 px-3 border border-gray-200 rounded-md">
                      <option value="">Sélectionnez</option>
                      {homeCommunes.map((c) => (
                        <option key={c.nom} value={c.nom}>{c.nom}</option>
                      ))}
                    </select>
                  ) : (
                    <Input id="contact-ville" type="text" required
                      value={contactForm.ville} onChange={(e) => setContactForm(prev => ({ ...prev, ville: e.target.value }))}
                      className="mt-2 text-lg py-5" placeholder="Votre ville" readOnly={homeCommunes.length === 1} />
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="contact-rfr" className="text-base font-semibold">Revenu fiscal de référence *</Label>
                <Input id="contact-rfr" type="number" required min="0"
                  value={contactForm.revenuFiscal} onChange={(e) => handleContactChange('revenuFiscal', e.target.value)}
                  className="mt-2 text-lg py-5" placeholder="Montant en euros" />
                <p className="text-xs text-gray-400 mt-1">Indiqué sur votre dernier avis d'imposition</p>
              </div>

              <div className="border-t pt-5 space-y-4">
                <p className="text-sm font-semibold text-[#033B5D]">Vos coordonnées pour être recontacté</p>
                <div>
                  <Label htmlFor="contact-nom" className="text-base font-semibold">Nom complet *</Label>
                  <Input id="contact-nom" required
                    value={contactForm.nom} onChange={(e) => handleContactChange('nom', e.target.value)}
                    onFocus={handleHomeFormFocus}
                    className="mt-2 text-lg py-5" placeholder="Votre nom" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact-telephone" className="text-base font-semibold">Téléphone *</Label>
                    <Input id="contact-telephone" type="tel" required
                      value={contactForm.telephone} onChange={(e) => handleContactChange('telephone', e.target.value)}
                      className="mt-2 text-lg py-5" placeholder="06 12 34 56 78" />
                  </div>
                  <div>
                    <Label htmlFor="contact-email" className="text-base font-semibold">Email *</Label>
                    <Input id="contact-email" type="email" required
                      value={contactForm.email} onChange={(e) => handleContactChange('email', e.target.value)}
                      className="mt-2 text-lg py-5" placeholder="votre@email.fr" />
                  </div>
                </div>
              </div>

              <Button type="submit"
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-6 text-lg font-semibold rounded-xl">
                <CheckCircle className="mr-2 h-5 w-5" />
                Vérifier mon éligibilité
              </Button>

              <p className="text-xs text-gray-500 text-center">
                En soumettant ce formulaire, vous acceptez d'être contacté par notre équipe
              </p>

              <div className="text-xs text-gray-400 text-center mt-3 space-y-1 border-t pt-3">
                <p className="font-semibold text-gray-500">Démarchage téléphonique – Information légale</p>
                <p>Le consommateur est informé qu'il peut s'inscrire gratuitement sur la liste d'opposition au démarchage téléphonique BLOCTEL : <a href="https://www.bloctel.gouv.fr" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#033B5D]">www.bloctel.gouv.fr</a></p>
                <p>Les informations recueillies sont traitées par SA ENERGO SYM.</p>
                <p>Pour en savoir plus et exercer vos droits, consultez notre <a href="/privacy" className="underline hover:text-[#033B5D]">Politique de confidentialité</a>.</p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Ma Prime Adapt' dans les médias */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#033B5D] mb-4">
              Ma Prime Adapt' : ce qu'en disent les médias
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Découvrez les reportages et articles de référence sur le dispositif d'aide à l'adaptation du logement
            </p>
          </div>

          {/* Reportages vidéo - Carrousel horizontal */}
          <h3 className="text-xl font-bold text-[#033B5D] mb-6 max-w-6xl mx-auto">Reportages et vidéos TV</h3>
          <div className="relative max-w-6xl mx-auto mb-14">
            <button
              onClick={() => videoScrollRef.current?.scrollBy({ left: -400, behavior: 'smooth' })}
              data-testid="video-scroll-left"
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors hidden md:flex"
            >
              <ChevronLeft className="w-5 h-5 text-[#033B5D]" />
            </button>

            <div
              ref={videoScrollRef}
              className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {[
                { id: 'x8qsixz', platform: 'dailymotion', name: 'Dailymotion', title: 'Ma Prime Adapt\' : la nouvelle aide pour mieux vieillir chez soi', testid: 'video-main' },
                { id: 'xa1p33u', platform: 'dailymotion', name: 'RTL - Conseil Conso', title: 'Comment fonctionne MaPrimeAdapt\' — Salon des seniors 2026', testid: 'video-rtl' },
                { id: 'xa1sipq', platform: 'dailymotion', name: 'RMC', title: 'MaPrimeAdapt\' : reportage terrain et témoignages', testid: 'video-rmc' },
                { id: 'x9sfky6', platform: 'dailymotion', name: 'Anah Officiel', title: 'Vraies Confessions : adapter son logement avec MaPrimeAdapt\'', testid: 'video-anah' },
                { id: 'tu5i-sxeN64', platform: 'youtube', name: 'Anah - Conférence', title: 'MaPrimeAdapt\', un an après : bilan officiel (37 000 logements, 98 % satisfaction)', testid: 'video-conf' },
                { id: 'GBX44DA-OzI', platform: 'youtube', name: 'Sud Radio', title: 'MaPrimeAdapt\' : 23 000 logements adaptés, les explications', testid: 'video-sudradio' },
                { id: 'laRHX_5QH5I', platform: 'youtube', name: 'France 3', title: 'Adaptation du logement : témoignages et exemples concrets', testid: 'video-france3' },
              ].map((video) => (
                <VideoCard key={video.testid} video={video} />
              ))}
            </div>

            <button
              onClick={() => videoScrollRef.current?.scrollBy({ left: 400, behavior: 'smooth' })}
              data-testid="video-scroll-right"
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors hidden md:flex"
            >
              <ChevronRight className="w-5 h-5 text-[#033B5D]" />
            </button>
          </div>

          {/* Sources presse - Carrousel horizontal */}
          <h3 className="text-xl font-bold text-[#033B5D] mb-6 max-w-6xl mx-auto">Articles et sources de référence</h3>
          <div className="relative max-w-6xl mx-auto">
            <button
              onClick={() => scrollRef.current?.scrollBy({ left: -320, behavior: 'smooth' })}
              data-testid="media-scroll-left"
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors hidden md:flex"
            >
              <ChevronLeft className="w-5 h-5 text-[#033B5D]" />
            </button>

            <div
              ref={scrollRef}
              className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {[
                { href: 'https://www.lemonde.fr/argent/article/2024/01/19/maprimeadapt-qui-peut-beneficier-de-cette-nouvelle-aide-pour-adapter-son-logement-a-la-perte-d-autonomie_6211719_1657007.html', logo: 'https://www.google.com/s2/favicons?domain=lemonde.fr&sz=64', name: 'Le Monde', title: 'Qui peut bénéficier de cette nouvelle aide ?', id: 'lemonde' },
                { href: 'https://www.mercipourlinfo.fr/actualites/aides-et-allocations/ma-prime-adapt-laide-pour-adapter-votre-logement-a-la-perte-dautonomie-est-reconduite-1146896', logo: 'https://www.google.com/s2/favicons?domain=tf1.fr&sz=64', name: 'TF1 - Merci pour l\'info', title: 'L\'aide pour adapter votre logement reconduite', id: 'tf1' },
                { href: 'https://www.anah.gouv.fr/anatheque/maprimeadapt-mode-emploi', logo: 'https://www.google.com/s2/favicons?domain=anah.gouv.fr&sz=64', name: 'Anah', title: 'Le mode d\'emploi 2026', id: 'anah' },
                { href: 'https://solidarites.gouv.fr/maprimeadapt-nouvelle-aide-pour-adapter-son-logement-la-perte-dautonomie', logo: 'https://www.google.com/s2/favicons?domain=gouv.fr&sz=64', name: 'Solidarités.gouv.fr', title: 'Adapter son logement à la perte d\'autonomie', id: 'gouv' },
                { href: 'https://www.silvereco.fr/la-subvention-maprimeadapt-fete-ses-2-ans-un-bilan-solide-et-des-milliers-de-nouveaux-menages-eligibles-en-2026/', logo: 'https://www.google.com/s2/favicons?domain=silvereco.fr&sz=64', name: 'SilverEco', title: 'MaPrimeAdapt\' fête ses 2 ans : bilan solide', id: 'silvereco' },
                { href: 'https://www.batiactu.com/edito/maprimeadapt-mode-emploi-2026-a-ete-publie-73682.php', logo: 'https://www.google.com/s2/favicons?domain=batiactu.com&sz=64', name: 'Batiactu', title: 'Le mode d\'emploi 2026 publié', id: 'batiactu' },
                { href: 'https://www.monparcourshandicap.gouv.fr/aides/maprimeadapt', logo: 'https://www.google.com/s2/favicons?domain=monparcourshandicap.gouv.fr&sz=64', name: 'Mon Parcours Handicap', title: 'Conditions et montants', id: 'handicap' },
                { href: 'https://www.toute-la-franchise.com/news-maprime-adapt-confirme-son-impact-en-2025-nouvelles-perspectives-dometvie', logo: 'https://www.google.com/s2/favicons?domain=toute-la-franchise.com&sz=64', name: 'Toute la Franchise', title: 'Impact confirmé en 2025', id: 'franchise' },
                { href: 'https://www.lamutuellegenerale.fr/blog/mon-bien-etre/ma-prime-adapt-une-aide-pour-vieillir-chez-soi', logo: 'https://www.google.com/s2/favicons?domain=lamutuellegenerale.fr&sz=64', name: 'La Mutuelle Générale', title: 'Une aide pour vieillir chez soi', id: 'mutuelle' },
                { href: 'https://www.aide-sociale.fr/ma-prime-adapt/', logo: 'https://www.google.com/s2/favicons?domain=aide-sociale.fr&sz=64', name: 'Aide-sociale.fr', title: 'Guide complet des aides', id: 'aidesociale' },
                { href: 'https://www.direct-assurance.fr/assurance-sante/amenagement-logement-senior-ma-prime-adapt', logo: 'https://www.google.com/s2/favicons?domain=direct-assurance.fr&sz=64', name: 'Direct Assurance', title: 'Aménagement logement senior', id: 'directassurance' },
              ].map((source) => (
                <a
                  key={source.id}
                  href={source.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`media-source-${source.id}`}
                  className="group flex-shrink-0 w-72 snap-start bg-white rounded-2xl shadow-md p-5 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <img src={source.logo} alt={source.name} className="w-8 h-8 rounded" />
                    <span className="text-sm font-medium text-gray-500 truncate">{source.name}</span>
                  </div>
                  <h3 className="font-bold text-[#033B5D] text-sm leading-snug group-hover:text-[#055a8e] transition-colors">{source.title}</h3>
                </a>
              ))}
            </div>

            <button
              onClick={() => scrollRef.current?.scrollBy({ left: 320, behavior: 'smooth' })}
              data-testid="media-scroll-right"
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors hidden md:flex"
            >
              <ChevronRight className="w-5 h-5 text-[#033B5D]" />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#033B5D] mb-4">
                Questions fréquentes
              </h2>
              <p className="text-lg text-gray-600">
                Trouvez rapidement les réponses à vos questions
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-xl shadow-md border border-gray-100 px-6">
                  <AccordionTrigger className="text-left font-semibold text-[#033B5D] hover:text-[#044d75] py-5">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 pb-5 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-12">
              <p className="text-gray-600 mb-4">Vous avez d'autres questions ?</p>
              <Button
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                variant="outline"
                size="lg"
                className="border-2 border-[#033B5D] text-[#033B5D] hover:bg-[#033B5D] hover:text-white px-8 py-6 text-lg font-semibold rounded-xl"
              >
                Contactez-nous
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Prêt à adapter votre logement ?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Vérifiez gratuitement votre éligibilité aux aides en 2 minutes
          </p>
          <Button
            onClick={() => navigate('/verifier-eligibilite')}
            size="lg"
            className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Vérifier mon éligibilité maintenant
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default NewHome;